import React, { useState, useEffect } from 'react';
import { 
    format, getWeek, isSameDay, startOfWeek, 
    addMonths, subMonths, addWeeks, addDays,
    startOfYear, eachMonthOfInterval, setMonth, getMonth, addYears, subYears 
} from 'date-fns';
import { vi } from 'date-fns/locale';
import useDebounce from '../../../hooks/useDebounce';

interface CalendarProps {
    currentDate: Date;
    selectedDate: Date | null;
    onSelectWeek: (date: Date) => void;
    onMonthChange: (date: Date) => void;
}

type CalendarView = 'day' | 'month';

interface MonthSelectorProps {
    currentDate: Date;
    onSelectMonth: (date: Date) => void;
    onMonthChange: (date: Date) => void;
}

const MonthSelector: React.FC<MonthSelectorProps> = ({ currentDate, onSelectMonth, onMonthChange }) => {
    const year = currentDate.getFullYear();
    const currentMonthIndex = getMonth(currentDate);
    const [debouncedDate, setDebouncedDate] = useState(currentDate);

    useDebounce(() => onMonthChange(debouncedDate), 300, [debouncedDate]);

    const months = eachMonthOfInterval({
        start: startOfYear(currentDate),
        end: setMonth(startOfYear(currentDate), 11)
    });

    return (
        <div className="p-1">
            <div className="flex justify-between items-center mb-4">
                <button 
                    onClick={() => setDebouncedDate(subYears(currentDate, 1))} 
                    className="p-1 rounded hover:bg-gray-100"
                >{'<'}</button>
                <span className="font-semibold text-lg">{year}</span>
                <button 
                    onClick={() => setDebouncedDate(addYears(currentDate, 1))} 
                    className="p-1 rounded hover:bg-gray-100"
                >{'>'}</button>
            </div>

            <div className="grid grid-cols-3 gap-2 text-center">
                {months.map((month, index) => {
                    const isSelected = index === currentMonthIndex;
                    const monthName = format(month, "'Tháng' M", { locale: vi as any });
                    
                    return (
                        <button
                            key={monthName}
                            type="button"
                            className={`p-3 rounded-lg font-semibold text-sm transition-colors 
                                ${isSelected ? 'bg-blue-600 text-white' : 'hover:bg-blue-100 text-gray-900'}`
                            }
                            onClick={() => onSelectMonth(month)}
                        >
                            {monthName}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

const CalendarDaysMock: React.FC<CalendarProps> = ({ currentDate, selectedDate, onSelectWeek, onMonthChange }) => {
    const today = new Date();
    const currentMonthTitle = format(currentDate, 'MM/yyyy');
    const firstDayOfMonth = startOfWeek(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1), { weekStartsOn: 1 });
    
    const [hoveredWeek, setHoveredWeek] = useState<number | null>(null); 
    const [currentView, setCurrentView] = useState<CalendarView>('day');
    const [debouncedDate, setDebouncedDate] = useState(currentDate);

    useDebounce(() => onMonthChange(debouncedDate), 300, [debouncedDate]);

    const weeksToDisplay = Array.from({ length: 5 }, (_, i) => addWeeks(firstDayOfMonth, i));

    const handleSelectMonth = (date: Date) => {
        onMonthChange(date); 
        setCurrentView('day');
    }

    const renderDay = (day: Date, weekNumber: number) => { 
        const isSelectedWeek = selectedDate && getWeek(selectedDate, { weekStartsOn: 1 }) === weekNumber;
        const isToday = isSameDay(day, today);
        const isCurrentMonth = day.getMonth() === currentDate.getMonth();
        
        const isHovered = hoveredWeek === weekNumber;

        const baseClass = "w-9 h-9 flex items-center justify-center rounded-md text-sm cursor-pointer";
        
        let colorClass = isSelectedWeek 
            ? "bg-blue-600 text-white" 
            : isHovered 
                ? "bg-blue-100 text-gray-900" 
                : isCurrentMonth 
                    ? "text-gray-900 hover:bg-gray-100" 
                    : "text-gray-400";
        
        const todayDot = isToday ? (
            <div className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-red-500" />
        ) : null;

        return (
            <button
                key={day.toISOString()}
                type="button"
                className={`relative ${baseClass} ${colorClass}`}
                onClick={() => onSelectWeek(day)} 
                
                onMouseEnter={() => setHoveredWeek(weekNumber)}
                onMouseLeave={() => setHoveredWeek(null)}
            >
                {day.getDate()}
                {todayDot}
            </button>
        );
    };
    
    const weekDays = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];

    return (
        <div className="p-4 w-[350px] bg-white rounded-lg shadow-xl border">
            {currentView === 'day' ? (
                <div className="flex justify-between items-center mb-3">
                    <button onClick={() => setDebouncedDate(subMonths(currentDate, 1))} className="p-1 rounded hover:bg-gray-100">{'<'}</button>
                    <button 
                        className="font-semibold px-2 py-1 rounded-md hover:bg-gray-100"
                        onClick={() => setCurrentView('month')} 
                        type="button"
                    >
                        {currentMonthTitle} 
                    </button>
                    <button onClick={() => setDebouncedDate(addMonths(currentDate, 1))} className="p-1 rounded hover:bg-gray-100">{'>'}</button>
                </div>
            ) : (
                null
            )}
            
            {currentView === 'day' ? (
                <>
                    <div className="grid grid-cols-8 gap-1 text-xs font-semibold text-gray-500 text-center">
                        <div className="w-9 border-r-2 border-gray-300">Tuần</div> 
                        {weekDays.map(day => <div key={day} className="w-9">{day}</div>)}
                    </div>

                    {/* Lưới Ngày */}
                    {weeksToDisplay.map((weekStart, index) => {
                        const daysOfWeek = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
                        const weekNumber = getWeek(weekStart, { weekStartsOn: 1 });
                        const isSelectedWeek = selectedDate && getWeek(selectedDate, { weekStartsOn: 1 }) === weekNumber;
                        const isHovered = hoveredWeek === weekNumber;
                        
                        return (
                            <div key={index} className="grid grid-cols-8 gap-1 my-1">
                                {/* Cột số Tuần */}
                                <div 
                                    className={`w-9 h-9 flex items-center justify-center rounded-md text-sm font-semibold 
                                    ${isSelectedWeek ? 'bg-blue-600 text-white' : ''}
                                    ${isHovered ? 'bg-blue-100' : 'hover:bg-gray-100 cursor-pointer'} 
                                    border-r-2 border-gray-300`} 
                                    onClick={() => onSelectWeek(weekStart)}
                                    
                                    onMouseEnter={() => setHoveredWeek(weekNumber)}
                                    onMouseLeave={() => setHoveredWeek(null)}
                                >
                                    {weekNumber}
                                </div>
                                {/* Các ngày */}
                                {daysOfWeek.map(day => renderDay(day, weekNumber))}
                            </div>
                        );
                    })}
                     <div className="text-right mt-2">
                        <button 
                            className="text-blue-600 font-semibold text-sm p-1 hover:bg-blue-50 rounded"
                            onClick={() => onSelectWeek(today)}
                        >
                            Tuần này
                        </button>
                    </div>
                </>
            ) : (
                <MonthSelector 
                    currentDate={currentDate} 
                    onSelectMonth={handleSelectMonth} 
                    onMonthChange={onMonthChange}
                />
            )}
        </div>
    );
};

export default CalendarDaysMock;